import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Car, Clock, Trash2, X, Calendar, FileText, AlertCircle, Mail, Phone, Package2 } from 'lucide-react';
import { Customer, getCustomer } from '../lib/customers';
import { CustomerVehicle } from '../lib/customers';
import { Service, ServiceCategory } from '../lib/services';
import { createProposal, Proposal } from '../lib/proposals';
import { useBusiness } from '../contexts/BusinessContext';
import EditCustomerModal from './manage/EditCustomerModal';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectInventoryItemModal from './manage/SelectInventoryItemModal';
import { sendProposalLink } from '../lib/proposals';
import { useToast } from '../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { ProductPhotoService } from '../services/productPhotoService';

interface ServiceOption {
  id: string;
  vehicleId: string;
  vehicleName: string;
  name: string;
  originalName: string;
  description: string;
  originalDescription: string;
  priceOptionId: string;
  price: number; // This is the original price
  duration_minutes: number;
  isSelectable?: boolean;
  // Discount fields
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  discountAmount?: number;
  discountedPrice?: number;
}

interface ProductOption {
  id: string;
  vehicleId: string;
  vehicleName: string;
  name: string;
  acquisition_cost: number;
  price: number;
  quantity: number;
  category_name: string;
  // Discount fields
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  discountAmount?: number;
  discountedPrice?: number;
}

interface Offer {
  title: string;
  services: ServiceOption[];
  products?: ProductOption[];
}

interface CreateProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdProposal: Proposal) => void;
}

export default function CreateProposalModal({
  isOpen,
  onClose,
  onSuccess
}: CreateProposalModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('');
  const [expiryOption, setExpiryOption] = useState('30');
  const [customExpiryDate, setCustomExpiryDate] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [showOfferSection, setShowOfferSection] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<Record<string, string>>({});
  const [offers, setOffers] = useState<Offer[]>([]);
  const [currentOffer, setCurrentOffer] = useState<Offer | null>(null);
  const [offerTitle, setOfferTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isSelectInventoryItemModalOpen, setIsSelectInventoryItemModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  const [isCustomerValid, setIsCustomerValid] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      setTitle('Proposal');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when modal closes
      setTitle('');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
      setSelectedCustomer(null);
      setShowOfferSection(false);
      setSelectedServices([]);
      setSelectedProducts([]);
      setProductPhotos({});
      setCurrentVehicle(null);
      setOffers([]);
      setCurrentOffer(null);
      setOfferTitle('');
      setError(null);
    }
    setShowValidationMessages(false);
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Add this useEffect right after the existing useEffect blocks to handle the selectable service flag
  useEffect(() => {
    // Check and clean up localStorage when modal opens or closes
    if (!isSelectServiceModalOpen) {
      localStorage.removeItem('adding_selectable_service');
    }
  }, [isSelectServiceModalOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleAddService = (service: Service, priceOption: any, isSelectable: boolean = false) => {
    if (!currentVehicle) return;
    
    const newService = {
      id: service.id,
      vehicleId: currentVehicle.id,
      vehicleName: `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`,
      name: isSelectable ? `${service.name} (Optional)` : service.name,
      originalName: service.name,
      description: service.description || '',
      originalDescription: service.description || '',
      priceOptionId: priceOption.id,
      price: priceOption.price, // This is the original price
      duration_minutes: priceOption.duration_minutes,
      isSelectable: isSelectable
    };

    setSelectedServices(prev => [...prev, newService]);
    setIsSelectServiceModalOpen(false);
  };

  const handleAddProduct = async (product: any) => {
    if (!currentVehicle) return;
    
    const newProduct: ProductOption = {
      id: product.id,
      vehicleId: currentVehicle.id,
      vehicleName: `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`,
      name: product.name,
      acquisition_cost: product.acquisition_cost,
      price: product.price,
      quantity: 1, // Default quantity
      category_name: product.category_name
    };

    setSelectedProducts(prev => [...prev, newProduct]);
    
    // Load product photo if available
    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds([product.id]);
      if (photoMap && photoMap[product.id] && photoMap[product.id].photo_url) {
        setProductPhotos(prev => ({
          ...prev,
          [product.id]: photoMap[product.id].photo_url
        }));
      }
    } catch (error) {
      console.error('Error loading product photo:', error);
    }
    
    setIsSelectInventoryItemModalOpen(false);
  };

  const handleRemoveService = (index: number) => {
    setSelectedServices(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handlePriceChange = (index: number, newPrice: number) => {
    setSelectedServices(prev => prev.map((service, i) => {
      if (i === index) {
        // Recalculate discount amount if discount exists
        let discountAmount = 0;
        if (service.discountValue && service.discountValue > 0) {
          if (service.discountType === 'percentage') {
            discountAmount = newPrice * (service.discountValue / 100);
          } else {
            discountAmount = service.discountValue;
          }
        }
        
        // Calculate discounted price for display
        const discountedPrice = Math.max(0, newPrice - discountAmount);
        
        return { 
          ...service, 
          price: newPrice, // This is the original price
          discountAmount,
          discountedPrice
        };
      }
      return service;
    }));
  };

  const handleServiceNameChange = (index: number, newName: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, name: newName } : service
    ));
  };

  const handleServiceDescriptionChange = (index: number, newDescription: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, description: newDescription } : service
    ));
  };

  const handleServiceDiscountChange = (index: number, discountType: 'percentage' | 'dollar', discountValue: number) => {
    setSelectedServices(prev => prev.map((service, i) => {
      if (i === index) {
        // Calculate discount amount for display purposes
        let discountAmount = 0;
        if (discountValue > 0) {
          if (discountType === 'percentage') {
            discountAmount = service.price * (discountValue / 100);
          } else {
            discountAmount = discountValue;
          }
        }
        
        // Calculate discounted price for display (price remains original price in database)
        const discountedPrice = Math.max(0, service.price - discountAmount);
        
        return {
          ...service,
          discountType,
          discountValue,
          discountAmount,
          discountedPrice // For display only
        };
      }
      return service;
    }));
  };



  const handleAddOffer = () => {
    setShowOfferSection(true);
    setOfferTitle('');
    setSelectedServices([]);
    setCurrentVehicle(null);
  };

  const handleEditOffer = (offer: Offer) => {
    setCurrentOffer(offer);
    setOfferTitle(offer.title);
    setSelectedServices(offer.services);
    setSelectedProducts(offer.products || []);
    
    // Load product photos for editing
    if (offer.products && offer.products.length > 0) {
      const loadProductPhotos = async () => {
        try {
          const productIds = offer.products!.map(p => p.id);
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(productIds);
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          setProductPhotos(prev => ({ ...prev, ...photoUrls }));
        } catch (error) {
          console.error('Error loading product photos for editing:', error);
        }
      };
      loadProductPhotos();
    }
    
    if (offer.services.length > 0) {
      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === offer.services[0].vehicleId);
      setCurrentVehicle(vehicle || null);
    } else if (offer.products && offer.products.length > 0) {
      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === offer.products![0].vehicleId);
      setCurrentVehicle(vehicle || null);
    }
    setShowOfferSection(true);
  };

  const handleSaveOffer = () => {
    if (offerTitle && (selectedServices.length > 0 || selectedProducts.length > 0)) {
      const offerData = {
        title: offerTitle,
        services: selectedServices,
        products: selectedProducts
      };

      if (currentOffer) {
        setOffers(prev => prev.map(offer => 
          offer === currentOffer ? offerData : offer
        ));
      } else {
        setOffers(prev => [...prev, offerData]);
      }
      setShowOfferSection(false);
      setOfferTitle('');
      setSelectedServices([]);
      setSelectedProducts([]);
      setProductPhotos({});
      setCurrentVehicle(null);
      setCurrentOffer(null);
    }
  };

  const handleRemoveOffer = (index: number) => {
    setOffers(prev => prev.filter((_, i) => i !== index));
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Update customer validation when customer changes
  useEffect(() => {
    setIsCustomerValid(!!selectedCustomer);
  }, [selectedCustomer]);

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    if (selectedCustomer?.id) {
      try {
        const updatedCustomer = await getCustomer(selectedCustomer.id);
        setSelectedCustomer(updatedCustomer);
      } catch (error) {
        console.error('Failed to refresh customer data:', error);
        // Fallback: manually add the vehicle to the current customer
        if (selectedCustomer && vehicle) {
          const updatedCustomer = {
            ...selectedCustomer,
            vehicles: [...(selectedCustomer.vehicles || []), vehicle]
          };
          setSelectedCustomer(updatedCustomer);
        }
      }
    }
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidationMessages(true);

    if (!selectedBusiness?.id || !isCustomerValid) {
      setError('Please select a customer');
      return;
    }

    if (offers.length === 0) {
      setError('Please add at least one offer');
      return;
    }

    // Calculate expiry date based on selection
    let expiryDate = new Date();
    if (showCustomDate && customExpiryDate) {
      expiryDate = new Date(customExpiryDate);
    } else {
      expiryDate.setDate(expiryDate.getDate() + parseInt(expiryOption));
    }

    try {
      setLoading(true);
      setError(null);

      const createdProposal = await createProposal({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer!.id,
        title,
        valid_until: expiryDate.toISOString(),
        offers: offers.map(offer => ({
          title: offer.title,
          services: offer.services.map(service => ({
            service_id: service.id,
            vehicle_id: service.vehicleId,
            name: service.name,
            description: service.description || '',
            price: service.price, // Original price
            discountType: service.discountType,
            discountValue: service.discountValue,
            tax_exempt: false,
            tax_rate: 5,
            duration_minutes: service.duration_minutes,
            isSelectable: service.isSelectable || false
          })),
          products: (offer.products || []).map(product => ({
            stock_item_id: product.id,
            vehicle_id: product.vehicleId,
            name: product.name,
            price: product.price,
            quantity: product.quantity,
            category_name: product.category_name,
            discountType: product.discountType,
            discountValue: product.discountValue
          }))
        }))
      });

      // Pass the created proposal to the onSuccess callback
      onSuccess?.(createdProposal);
      onClose();

      toast({
        title: 'Proposal Created',
        description: `Proposal "${title}" created successfully!`,
        duration: 3000, // 3 seconds
      });
    } catch (err) {
      console.error('Error creating proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[9999]`}
      onClick={handleClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] backdrop-blur-xl bg-white/80 transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  New Proposal
                </h2>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-0">
              {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Proposal Details Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Proposal Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proposal Title *
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter proposal title"
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date
                      </label>
                      <select
                        value={expiryOption}
                        onChange={(e) => {
                          setExpiryOption(e.target.value);
                          setShowCustomDate(e.target.value === 'custom');
                        }}
                        className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="60">60 days from now</option>
                        <option value="30">30 days from now</option>
                        <option value="15">15 days from now</option>
                        <option value="custom">Custom date</option>
                      </select>
                    </div>
                    
                    {showCustomDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Date
                        </label>
                        <input
                          type="date"
                          value={customExpiryDate}
                          onChange={(e) => setCustomExpiryDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Selection Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Selection</h3>
                
                {selectedCustomer ? (
                  <div 
                    className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setIsEditCustomerModalOpen(true)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                        {selectedCustomer.first_name[0].toLowerCase()}{selectedCustomer.last_name[0].toLowerCase()}
                      </div>
                      <div>
                        <div className="text-base font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          {selectedCustomer.email && (
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span>{selectedCustomer.email}</span>
                            </div>
                          )}
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Select Customer
                  </button>
                )}
              </div>

              {/* Offers Section */}
              {selectedCustomer && (
                <div className="bg-white border-b border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Offers</h3>

                  {/* Existing Offers List */}
                  {offers.length > 0 && (
                    <div className="mb-6 space-y-4">
                      {offers.map((offer, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-gray-900">{offer.title}</h4>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditOffer(offer);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newOffer = {
                                    title: `${offer.title} (Copy)`,
                                    services: offer.services.map(service => ({
                                      ...service,
                                      id: service.id
                                    })),
                                    products: offer.products ? offer.products.map(product => ({
                                      ...product,
                                      id: product.id
                                    })) : []
                                  };
                                  setOffers(prev => [...prev, newOffer]);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                Duplicate
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveOffer(index);
                                }}
                                className="text-xs font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {/* Display regular services first */}
                            {offer.services
                              .filter(service => !service.isSelectable)
                              .map((service, serviceIndex) => (
                              <div key={`preview-regular-${serviceIndex}`} className="text-sm text-gray-600 font-medium">
                                <span>• {service.name} - ${service.price.toLocaleString()} ({service.vehicleName})</span>
                              </div>
                            ))}
                            
                            {/* Show a divider if there are both regular and optional services */}
                            {offer.services.some(s => !s.isSelectable) && offer.services.some(s => s.isSelectable) && (
                              <div className="text-xs text-gray-500 pt-1 border-t border-purple-200/30 mt-1 font-bold">Optional Services</div>
                            )}
                            
                            {/* Display optional services next */}
                            {offer.services
                              .filter(service => service.isSelectable)
                              .map((service, serviceIndex) => (
                              <div key={`preview-optional-${serviceIndex}`} className="text-sm text-gray-500 font-medium">
                                <div className="flex items-center">
                                  <div className="w-3 h-3 mr-1 border border-purple-300 rounded-sm bg-purple-100"></div>
                                  <span className="text-gray-500">• {service.name} - ${service.price.toLocaleString()} (Optional)</span>
                                </div>
                              </div>
                            ))}
                            
                            {/* Display products if any */}
                            {offer.products && offer.products.length > 0 && (
                              <>
                                {/* Show a divider if there are services above */}
                                {offer.services.length > 0 && (
                                  <div className="text-xs text-gray-500 pt-1 border-t border-green-200/30 mt-1 font-bold">Products</div>
                                )}
                                
                                {offer.products.map((product, productIndex) => (
                                  <div key={`preview-product-${productIndex}`} className="text-sm text-green-600 font-medium">
                                    <div className="flex items-center">
                                      <Package2 className="h-3 w-3 mr-1 text-green-500" />
                                      <span>• {product.name} (Qty: {product.quantity}) - ${(product.price * product.quantity).toFixed(2)} ({product.vehicleName})</span>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!showOfferSection && (
                    <button
                      type="button"
                      onClick={handleAddOffer}
                      className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    >
                      <Plus className="h-5 w-5 mr-2 inline-block" />
                      Create Offer
                    </button>
                  )}

                  {/* Offer Creation/Edit Section */}
                  {showOfferSection && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Offer Title *
                        </label>
                        <input
                          type="text"
                          value={offerTitle}
                          onChange={(e) => setOfferTitle(e.target.value)}
                          placeholder="Enter offer title"
                          className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>

                      {/* Vehicle Selection */}
                      {!currentVehicle ? (
                        <button
                          type="button"
                          onClick={() => setIsSelectVehicleModalOpen(true)} 
                          className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Select Vehicle
                        </button>
                      ) : (
                        <div className="space-y-4">
                          {/* Group services by vehicle */}
                          {Object.entries(
                            selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
                              if (!acc[service.vehicleId]) {
                                acc[service.vehicleId] = [];
                              }
                              acc[service.vehicleId].push(service);
                              return acc;
                            }, {})
                          ).map(([vehicleId, vehicleServices]) => (
                            <div key={vehicleId} className="bg-white rounded-lg border border-gray-200 p-4">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                                    <Car className="h-6 w-6 text-white" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {vehicleServices[0].vehicleName}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Services for this vehicle */}
                              <div className="space-y-4">
                                {/* Display regular services first */}
                                {vehicleServices
                                  .filter(service => !service.isSelectable)
                                  .map((service, index) => (
                                  <div key={`regular-${index}`} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <input
                                          type="text"
                                          value={service.name}
                                          onChange={(e) => handleServiceNameChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          )}
                                          className="text-sm font-bold text-gray-900 border-none bg-transparent focus:ring-0 w-full"
                                          placeholder="Enter service name"
                                        />
                                        <textarea
                                          value={service.description}
                                          onChange={(e) => handleServiceDescriptionChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          )}
                                          className="mt-1 text-sm text-gray-600 w-full border-none bg-transparent focus:ring-0 resize-none font-medium"
                                          placeholder="Enter service description"
                                          rows={2}
                                        />
                                        <div className="mt-1 flex items-center text-xs text-gray-500 font-bold">
                                          <Clock className="h-3.5 w-3.5 mr-1" />
                                          {service.duration_minutes >= 1440 
                                            ? `${Math.round(service.duration_minutes / 1440)} days`
                                            : `${Math.round(service.duration_minutes / 60)} hours`
                                          }
                                        </div>
                                        
                                        <div className="mt-2 space-y-2">
                                          {/* Price Input */}
                                          <div className="flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 font-bold">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            {service.discountValue && service.discountValue > 0 && (
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-500 font-medium">→</span>
                                                <span className="text-sm font-bold text-green-600">
                                                  ${service.discountedPrice?.toLocaleString()} 
                                                  <span className="text-xs text-gray-500 ml-1">
                                                    (after {service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`} discount)
                                                  </span>
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          {/* Discount Controls */}
                                          <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-600 font-medium">Discount:</span>
                                            <select
                                              value={service.discountType || 'percentage'}
                                              onChange={(e) => {
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                handleServiceDiscountChange(
                                                  serviceIndex,
                                                  e.target.value as 'percentage' | 'dollar',
                                                  service.discountValue || 0
                                                );
                                              }}
                                              className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            >
                                              <option value="percentage">%</option>
                                              <option value="dollar">$</option>
                                            </select>
                                            <input
                                              type="number"
                                              value={service.discountValue || 0}
                                              onChange={(e) => {
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                handleServiceDiscountChange(
                                                  serviceIndex,
                                                  service.discountType || 'percentage',
                                                  parseFloat(e.target.value) || 0
                                                );
                                              }}
                                              placeholder="0"
                                              className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            {service.discountValue && service.discountValue > 0 && (
                                              <span className="text-xs text-green-600 font-medium">
                                                -{service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                        className="p-2 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50/80 transition-all"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}

                                {/* Display selectable (optional) services next */}
                                {vehicleServices.filter(service => service.isSelectable).length > 0 && (
                                  <div className="border-t border-purple-200/30 pt-3 mt-3 mb-2">
                                    <div className="text-center text-sm text-purple-700 font-bold bg-purple-50/80 py-2 rounded-xl border border-purple-200/30">
                                      Optional Add-on Services
                                    </div>
                                  </div>
                                )}
                                
                                {vehicleServices
                                  .filter(service => service.isSelectable)
                                  .map((service, index) => (
                                  <div key={`optional-${index}`} className="bg-white/60 backdrop-blur-sm rounded-2xl p-3 border border-purple-200/20 opacity-80"
                                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="mb-2 flex items-center">
                                          <div className="w-10 h-5 bg-purple-200 rounded-full p-1 mr-2">
                                            <div className="bg-purple-500 w-3 h-3 rounded-full"></div>
                                          </div>
                                          <span className="text-xs text-purple-600 font-bold">Optional Add-on</span>
                                        </div>
                                        <input
                                          type="text"
                                          value={service.name}
                                          onChange={(e) => handleServiceNameChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          )}
                                          className="text-sm font-bold text-gray-900 border-none bg-transparent focus:ring-0 w-full"
                                          placeholder="Enter service name"
                                        />
                                        <textarea
                                          value={service.description}
                                          onChange={(e) => handleServiceDescriptionChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          )}
                                          className="mt-1 text-sm text-gray-600 w-full border-none bg-transparent focus:ring-0 resize-none font-medium"
                                          placeholder="Enter service description"
                                          rows={2}
                                        />
                                        <div className="mt-1 flex items-center text-xs text-gray-500 font-bold">
                                          <Clock className="h-3.5 w-3.5 mr-1" />
                                          {service.duration_minutes >= 1440 
                                            ? `${Math.round(service.duration_minutes / 1440)} days`
                                            : `${Math.round(service.duration_minutes / 60)} hours`
                                          }
                                        </div>
                                        
                                        <div className="mt-2 space-y-2">
                                          {/* Price Input */}
                                          <div className="flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 font-bold">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            {service.discountValue && service.discountValue > 0 && (
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-500 font-medium">→</span>
                                                <span className="text-sm font-bold text-green-600">
                                                  ${service.price.toLocaleString()} 
                                                  <span className="text-xs text-gray-500 ml-1">
                                                    (after {service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`} discount)
                                                  </span>
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          {/* Discount Controls */}
                                          <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-600 font-medium">Discount:</span>
                                            <select
                                              value={service.discountType || 'percentage'}
                                              onChange={(e) => {
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                handleServiceDiscountChange(
                                                  serviceIndex,
                                                  e.target.value as 'percentage' | 'dollar',
                                                  service.discountValue || 0
                                                );
                                              }}
                                              className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            >
                                              <option value="percentage">%</option>
                                              <option value="dollar">$</option>
                                            </select>
                                            <input
                                              type="number"
                                              value={service.discountValue || 0}
                                              onChange={(e) => {
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                handleServiceDiscountChange(
                                                  serviceIndex,
                                                  service.discountType || 'percentage',
                                                  parseFloat(e.target.value) || 0
                                                );
                                              }}
                                              placeholder="0"
                                              className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            {service.discountValue && service.discountValue > 0 && (
                                              <span className="text-xs text-green-600 font-medium">
                                                -{service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                        className="p-2 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50/80 transition-all"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}

                                {/* Display products for this vehicle */}
                                {selectedProducts.filter(product => product.vehicleId === vehicleId).length > 0 && (
                                  <>
                                    <div className="border-t border-green-200/30 pt-3 mt-3 mb-2">
                                      <div className="text-center text-sm text-green-700 font-bold bg-green-50/80 py-2 rounded-xl border border-green-200/30">
                                        Products
                                      </div>
                                    </div>
                                    
                                    {selectedProducts
                                      .filter(product => product.vehicleId === vehicleId)
                                      .map((product, index) => (
                                      <div key={`product-${index}`} className="bg-green-50/60 backdrop-blur-sm rounded-2xl p-3 border border-green-200/20 relative">
                                        {/* Delete button positioned absolutely in top-right */}
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveProduct(selectedProducts.findIndex(p => p === product))}
                                          className="absolute top-2 right-2 p-1.5 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50/80 transition-all"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                        
                                        <div className="flex items-start space-x-3 pr-8">
                                          {/* Product Icon/Photo */}
                                          <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                                            {productPhotos[product.id] ? (
                                              <img
                                                src={productPhotos[product.id]}
                                                alt={product.name}
                                                className="h-10 w-10 rounded-lg object-cover"
                                              />
                                            ) : (
                                              <Package2 className="h-6 w-6 text-green-600" />
                                            )}
                                          </div>
                                          
                                          <div className="flex-1">
                                            <div className="text-sm font-bold text-gray-900 mb-1">
                                              {product.name}
                                            </div>
                                            <div className="text-xs text-gray-500 mb-2">
                                              Category: {product.category_name}
                                            </div>
                                            
                                            {/* Quantity and Price */}
                                            <div className="flex items-center space-x-4 mb-2">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600 font-medium">Qty:</span>
                                                <input
                                                  type="number"
                                                  value={product.quantity}
                                                  onChange={(e) => {
                                                    const newQuantity = parseInt(e.target.value) || 1;
                                                    setSelectedProducts(prev => prev.map((p) => 
                                                      selectedProducts.findIndex(sp => sp === p) === selectedProducts.findIndex(sp => sp === product) 
                                                        ? { ...p, quantity: newQuantity } 
                                                        : p
                                                    ));
                                                  }}
                                                  min="1"
                                                  className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                                                />
                                              </div>
                                              
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600 font-medium">Price:</span>
                                                <input
                                                  type="number"
                                                  value={product.price}
                                                  onChange={(e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    setSelectedProducts(prev => prev.map((p) => 
                                                      selectedProducts.findIndex(sp => sp === p) === selectedProducts.findIndex(sp => sp === product) 
                                                        ? { ...p, price: newPrice } 
                                                        : p
                                                    ));
                                                  }}
                                                  step="0.01"
                                                  className="w-20 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                                                />
                                              </div>
                                            </div>
                                            
                                            {/* Total on separate line */}
                                            <div className="text-xs text-green-600 font-bold">
                                              Total: ${(product.price * product.quantity).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>

                              {/* Add Service and Product Buttons for this vehicle */}
                              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                    if (vehicle) {
                                      setCurrentVehicle(vehicle);
                                      setIsSelectServiceModalOpen(true);
                                    }
                                  }}
                                  className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Service
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                    if (vehicle) {
                                      setCurrentVehicle(vehicle);
                                      localStorage.setItem('adding_selectable_service', 'true');
                                      setIsSelectServiceModalOpen(true);
                                    }
                                  }}
                                  className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Optional Service
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                    if (vehicle) {
                                      setCurrentVehicle(vehicle);
                                      setIsSelectInventoryItemModalOpen(true);
                                    }
                                  }}
                                  className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-green-300 rounded-lg text-sm font-medium text-green-600 hover:text-green-800 hover:border-green-400 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                >
                                  <Package2 className="h-4 w-4 mr-2" />
                                  Add Product
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Add Another Vehicle Button */}
                          <button
                            type="button"
                            onClick={() => setIsSelectVehicleModalOpen(true)}
                            className="mt-4 w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          >
                            <Plus className="h-5 w-5 mr-2" />
                            Add Another Vehicle
                          </button>
                        </div>
                      )}



                      {/* Pricing Summary */}
                      {(selectedServices.length > 0 || selectedProducts.length > 0) && (
                        <div className="mt-6 pt-6 border-t border-purple-200/30">
                          <h4 className="text-sm font-bold text-gray-900 mb-4">Pricing Summary</h4>
                          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-purple-200/20"
                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                            <div className="space-y-2">
                              {(() => {
                                // Calculate service totals
                                const serviceSubtotal = selectedServices.reduce((sum, service) => sum + service.price, 0);
                                const serviceDiscount = selectedServices.reduce((sum, service) => sum + (service.discountAmount || 0), 0);
                                
                                // Calculate product totals
                                const productSubtotal = selectedProducts.reduce((sum, product) => sum + (product.price * product.quantity), 0);
                                const productDiscount = selectedProducts.reduce((sum, product) => sum + (product.discountAmount || 0), 0);
                                
                                // Combined totals
                                const originalSubtotal = serviceSubtotal + productSubtotal;
                                const totalDiscount = serviceDiscount + productDiscount;
                                const currentSubtotal = originalSubtotal - totalDiscount;
                                const gst = Math.round((currentSubtotal * 0.05) * 100) / 100; // Round to nearest penny
                                const total = Math.round((currentSubtotal + gst) * 100) / 100; // Round to nearest penny

                                return (
                                  <>
                                    <div className="flex justify-between text-sm text-gray-600 font-medium">
                                      <span>Original Subtotal</span>
                                      <span>${originalSubtotal.toLocaleString()}</span>
                                    </div>
                                    {totalDiscount > 0 && (
                                      <div className="flex justify-between text-sm text-green-600 font-medium">
                                        <span>Total Discounts Applied</span>
                                        <span>- ${totalDiscount.toLocaleString()}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-sm text-gray-900 font-medium">
                                      <span>Subtotal After Discounts</span>
                                      <span>${currentSubtotal.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-600 font-medium">
                                      <span>GST (5%)</span>
                                      <span>+ ${gst.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-purple-200/30">
                                      <span>Total</span>
                                      <span>${total.toLocaleString()}</span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowOfferSection(false);
                            setOfferTitle('');
                            setSelectedServices([]);
                            setCurrentVehicle(null);
                            setCurrentOffer(null);
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveOffer}
                          disabled={!offerTitle || selectedServices.length === 0}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Save Offer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="bg-white border-t border-gray-200 p-6">
                <button
                  type="submit"
                  disabled={loading || !title || !isCustomerValid || (!showOfferSection && offers.length === 0)}
                  className={`w-full px-6 py-3 text-base font-semibold text-white rounded-lg transition-colors ${
                    (title && isCustomerValid && (showOfferSection || offers.length > 0)) 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                      Creating Proposal...
                    </div>
                  ) : (
                    'Create Proposal'
                  )}
                </button>
                
                {showValidationMessages && !isCustomerValid && (
                  <p className="mt-2 text-xs text-center text-gray-500 font-medium">Please select a customer</p>
                )}
                {showValidationMessages && isCustomerValid && !showOfferSection && offers.length === 0 && (
                  <p className="mt-2 text-xs text-center text-gray-500 font-medium">Please add at least one offer</p>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setIsSelectCustomerModalOpen(false);
        }} 
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setSelectedCustomer(updatedCustomer);
          setIsEditCustomerModalOpen(false);
        }}
        customer={selectedCustomer}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={(vehicle) => {
          setCurrentVehicle(vehicle);
          setIsSelectServiceModalOpen(true);
          setIsSelectVehicleModalOpen(false); 
        }}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => ({
          id: s.vehicleId,
          make: s.vehicleName.split(' ')[1] || '',
          model: s.vehicleName.split(' ')[2] || '',
          year: parseInt(s.vehicleName.split(' ')[0]) || new Date().getFullYear()
        })) as any}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          if (currentVehicle) {
            const isSelectable = localStorage.getItem('adding_selectable_service') === 'true';
            handleAddService(service, priceOption, isSelectable);
            localStorage.removeItem('adding_selectable_service');
          }
        }}
      />

      <SelectInventoryItemModal
        isOpen={isSelectInventoryItemModalOpen}
        onClose={() => setIsSelectInventoryItemModalOpen(false)}
        onAdd={handleAddProduct}
        currentBookingProducts={selectedProducts.map(p => ({
          product: {
            id: p.id,
            name: p.name,
            quantity: p.quantity
          }
        }))}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={selectedCustomer?.id || ''}
      />
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures consistent behavior
  // whether triggered from FAB menu or Proposals page button
  return createPortal(modalContent, document.body);
}